import {
  CheckCircle,
  CloudUpload,
  Delete,
  Download,
  Edit,
  Refresh,
  Send,
  TaskAlt,
  Undo,
} from "@mui/icons-material";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import { useInvoices } from "../../api/invoices";
import InvoiceForm from "../../components/invoices/invoiceForm.component";
import CustomModal from "../../components/ui/modal/modal.component";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import {
  ERROR_SNACKBAR,
  INFO_SNACKBAR,
  INVOICES_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { Invoice } from "../../types/invoices";
import { Action } from "../../types/toolbar";

export const InvoiceDetailsActions = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { get, remove, loading: crudLoading } = useCRUD(INVOICES_ENDPOINT);
  const {
    issueInvoice,
    regenerateInvoicePdf,
    sendInvoice,
    sendInvoiceToKsef,
    markInvoicePaid,
    downloadInvoicePdf,
    loading,
  } = useInvoices();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [editOpened, setEditOpened] = useState<boolean>(false);
  const [deleteOpened, setDeleteOpened] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const canChange = useHasPermission("invoice.change_invoice");
  const canSend = useHasPermission("invoice.send_invoice");
  const canSubmitToKsef = useHasPermission("invoice.submit_invoice_to_ksef");
  const canMarkPaid = useHasPermission("invoice.mark_invoice_paid");
  const canDelete = useHasPermission("invoice.delete_invoice");

  const fetchInvoice = useCallback(async () => {
    if (!id) {
      return;
    }
    const response = await get(id);
    setInvoice(response?.data ?? null);
  }, [id, get]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const announceChange = () => {
    window.dispatchEvent(new Event("invoiceUpdated"));
    fetchInvoice();
  };

  const runAction = async (
    action: () => Promise<any>,
    successMessage: string
  ) => {
    const result = await action();
    if (result?.success) {
      enqueueSnackbar(successMessage, SUCCESS_SNACKBAR);
      announceChange();
      return;
    }

    enqueueSnackbar(
      result?.errorData?.message ||
        t("There was an error, please review the form."),
      ERROR_SNACKBAR
    );
  };

  const handleDelete = async () => {
    setDeleteError(null);
    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t("Invoice has been deleted"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("invoiceUpdated"));
      navigate("/invoices");
      return;
    }

    setDeleteError(
      result?.errorData?.message || t("Could not delete the invoice")
    );
  };

  if (!invoice || !id) {
    return null;
  }

  const isDraft = invoice.status === "D";
  const isIssued = invoice.status === "I";
  const ksefSubmittable =
    invoice.ksef_status === "N" || invoice.ksef_status === "R";

  const actions: Action[] = [
    {
      id: "edit-invoice",
      icon: <Edit />,
      label: t("Edit invoice"),
      onClick: () => setEditOpened(true),
      // An issued invoice is a legal document; the backend refuses edits too.
      disabled: !canChange || !isDraft,
    },
    {
      id: "issue-invoice",
      icon: <TaskAlt />,
      label: t("Issue invoice"),
      onClick: () =>
        runAction(() => issueInvoice(id), t("Invoice has been issued")),
      disabled: !canChange || !isDraft || loading,
    },
    {
      id: "download-pdf",
      icon: <Download />,
      label: t("Download PDF"),
      onClick: async () => {
        const result = await downloadInvoicePdf(id, invoice);
        if (!result.success) {
          enqueueSnackbar(t("Could not download the PDF"), ERROR_SNACKBAR);
        }
      },
      disabled: loading,
    },
    {
      id: "regenerate-invoice-pdf",
      icon: <Refresh />,
      label: t("Regenerate PDF"),
      onClick: () =>
        runAction(
          () => regenerateInvoicePdf(id),
          t("PDF has been regenerated")
        ),
      // Only worth offering once a PDF exists; before that "Download PDF"
      // renders one anyway.
      disabled: !canChange || !invoice.has_pdf || loading,
    },
    {
      id: "send-invoice",
      icon: <Send />,
      label: t("Send to client"),
      onClick: () =>
        runAction(
          () => sendInvoice(id),
          t("Invoice has been sent to the client")
        ),
      disabled: !canSend || !isIssued || loading,
    },
    {
      id: "send-to-ksef",
      icon: <CloudUpload />,
      label: t("Send to KSeF"),
      onClick: async () => {
        const result = await sendInvoiceToKsef(id);
        if (result?.success) {
          // Submission runs in the background: it opens a KSeF session and
          // polls the Ministry, which outlasts an HTTP request.
          enqueueSnackbar(t("Invoice queued for KSeF"), INFO_SNACKBAR);
          announceChange();
          return;
        }
        enqueueSnackbar(
          result?.errorData?.message || t("Could not queue the invoice"),
          ERROR_SNACKBAR
        );
      },
      disabled: !canSubmitToKsef || !isIssued || !ksefSubmittable || loading,
    },
    {
      id: "toggle-paid",
      icon: invoice.is_paid ? <Undo /> : <CheckCircle />,
      label: invoice.is_paid ? t("Mark as unpaid") : t("Mark as paid"),
      onClick: () =>
        runAction(
          () => markInvoicePaid(id, !invoice.is_paid),
          invoice.is_paid
            ? t("Invoice marked as unpaid")
            : t("Invoice marked as paid")
        ),
      disabled: !canMarkPaid || !isIssued || loading,
    },
    {
      id: "delete-invoice",
      icon: <Delete />,
      label: t("Delete invoice"),
      onClick: () => {
        setDeleteError(null);
        setDeleteOpened(true);
      },
      color: "var(--errorColor)",
      // A KSeF-accepted invoice cannot be withdrawn from the Ministry's
      // system, so its local record is kept; the backend refuses too.
      disabled: !canDelete || invoice.ksef_status === "A" || loading,
    },
  ];

  const deleteModalContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Deleting this invoice will permanently remove it from the system. This action cannot be undone."
        )}
      </Typography>

      {isIssued && (
        <Alert severity="warning">
          <Typography variant="body2">
            {t(
              "This invoice has already been issued and its number will be left unused."
            )}
          </Typography>
        </Alert>
      )}

      {deleteError && <Alert severity="error">{deleteError}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDelete}
        disabled={crudLoading}
      >
        {crudLoading ? t("Processing...") : t("Confirm deletion")}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={deleteOpened}
        setOpened={setDeleteOpened}
        title={t("Delete invoice")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={editOpened}
        setOpened={setEditOpened}
        title={t("Edit invoice")}
        content={
          <InvoiceForm
            id={id}
            onSuccess={() => {
              setEditOpened(false);
              announceChange();
            }}
          />
        }
      />
    </>
  );
};
